<template>
  <div class="diff-viewer" v-html="highlightedDiff"></div>
</template>

<script setup>
import { computed } from 'vue'
import { diff_match_patch, DIFF_INSERT, DIFF_DELETE, DIFF_EQUAL } from 'diff-match-patch'

const props = defineProps({
  oldHtml: { type: String, required: true },
  newHtml: { type: String, required: true }
})


function wrapDiffPreservingBlocks(text, className) {
  // Split text by any HTML tag (opening or closing)
  const parts = text.split(/(<\/?[^>]+>)/);

  return parts
    .map(part => {
      // If part is a tag, return as-is
      if (part.startsWith('<') && part.endsWith('>')) {
        return part;
      }
      // If part is empty or whitespace only, preserve as-is
      if (part.trim() === '') {
        return part;
      }
      // Otherwise wrap text in span
      return `<span class="${className}">${part}</span>`;
    })
    .join('');
}

// Use private-use Unicode chars starting from \uE000
const BASE_CHAR_CODE = 0xe000

function tokenizeHtml(html) {
  return html.match(/<[^>]+>|[^<]+/g) || []
}

function replaceTagsWithPlaceholders(tokens) {
  const tagMap = new Map()
  const textParts = []
  let counter = 0

  for (const token of tokens) {
    if (token.startsWith('<')) {
      if (!tagMap.has(token)) {
        const placeholder = String.fromCharCode(BASE_CHAR_CODE + counter++)
        tagMap.set(token, placeholder)
      }
      textParts.push(tagMap.get(token))
    } else {
      textParts.push(token)
    }
  }

  return { text: textParts.join(''), tagMap }
}

function sanitizeHtml(html) {
  const div = document.createElement('div')
  div.innerHTML = html
  return div.innerHTML
}

// Setup diff-match-patch
const dmp = new diff_match_patch()

const highlightedDiff = computed(() => {
  
  const oldTokens = tokenizeHtml(props.oldHtml || "")
  const newTokens = tokenizeHtml(props.newHtml || "")

  const { text: oldText, tagMap: oldMap } = replaceTagsWithPlaceholders(oldTokens)
  const { text: newText, tagMap: newMap } = replaceTagsWithPlaceholders(newTokens)

  const tagMap = new Map([...oldMap, ...newMap])
  const inverseMap = new Map([...tagMap.entries()].map(([k, v]) => [v, k]))
  function restorePlaceholders(str, tagMap) {
    return str.split('').map(ch => inverseMap.get(ch) || ch).join('')
  }


  const diffs = dmp.diff_main(oldText, newText)
  dmp.diff_cleanupSemantic(diffs)

  let html = diffs.map(([type, text]) => {
    const isTagOnlyChange =  inverseMap.has(text)
    text = restorePlaceholders(text, tagMap)
    if (type === DIFF_INSERT) return wrapDiffPreservingBlocks(text, 'diff-mark diff-add')
    if (type === DIFF_DELETE) return wrapDiffPreservingBlocks(text, 'diff-mark diff-remove')
    // if (type === DIFF_DELETE)  {
    //   if (!isTagOnlyChange) {        
    //     return wrapDiffPreservingBlocks(text, 'diff-mark diff-remove')
    //   } 
    // }
    return text
  }).join('')


  return sanitizeHtml(html)
})
</script>

<style scoped>
.diff-viewer {
  line-height: 1.6;
}
</style>
